import { useTranslations } from "@/i18n";
import type { MessageId } from "@/i18n/catalogs/en";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
} from "react";
import { FiLogIn, FiMenu, FiUserPlus, FiX } from "react-icons/fi";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Brand } from "@/components/layout/Brand";
import { authenticatedNavigation } from "@/components/layout/navigation";
import { Sidebar } from "@/components/layout/Sidebar";
import { UserControls } from "@/components/layout/UserControls";
import { LanguageSelector } from "@/components/layout/LanguageSelector";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { GlobalReadingTimer } from "@/components/reading/GlobalReadingTimer";
import { QuickReadingAction } from "@/components/reading/QuickReadingAction";
import { useAuth } from "@/hooks/useAuth";
const mobileLinkClass = ({ isActive }: { isActive: boolean }): string =>
  `flex min-h-14 items-center gap-4 rounded-xl px-4 font-semibold focus:outline-none ${isActive ? "bg-accent/15 text-ink" : "text-body hover:bg-accent/10 hover:text-highlight"}`;
const MOBILE_PRIMARY_PATHS: ReadonlySet<string> = new Set([
  "/dashboard",
  "/books",
  "/reading",
  "/stats",
]);
const mobilePrimaryNavigation = authenticatedNavigation.filter(({ path }) =>
  MOBILE_PRIMARY_PATHS.has(path)
);
const TEXT_ENTRY_INPUT_TYPES: ReadonlySet<string> = new Set([
  "email",
  "number",
  "password",
  "search",
  "tel",
  "text",
  "url",
]);

function isTextEntryControl(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLTextAreaElement || target.isContentEditable) return true;
  return target instanceof HTMLInputElement && TEXT_ENTRY_INPUT_TYPES.has(target.type);
}

export function Layout() {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const t = useTranslations();
  const location = useLocation();
  const navigate = useNavigate();
  const menuId = useId();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isTextEntryFocused, setIsTextEntryFocused] = useState<boolean>(false);
  const [routeAnnouncement, setRouteAnnouncement] = useState<string>("");
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const firstMenuLinkRef = useRef<HTMLAnchorElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const previousPathRef = useRef<string>(location.pathname);
  useEffect(() => setIsMenuOpen(false), [location.pathname]);
  useEffect(() => {
    if (isMenuOpen) firstMenuLinkRef.current?.focus();
  }, [isMenuOpen]);
  useEffect(() => {
    const routeLabel = getRouteLabel(location.pathname, t);
    document.title = `${routeLabel} | Celsus`;
    if (previousPathRef.current !== location.pathname) {
      previousPathRef.current = location.pathname;
      setRouteAnnouncement(t("common.Layout.pageLoaded", { route: routeLabel }));
      window.requestAnimationFrame(() => mainRef.current?.focus());
    }
  }, [location.pathname, t]);
  useEffect(() => {
    if (!isMenuOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMenuOpen]);
  const closeMenu = (restoreFocus = false): void => {
    setIsMenuOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() => menuButtonRef.current?.focus());
    }
  };
  const handleLogout = (): void => {
    logout();
    navigate("/");
  };
  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeMenu(true);
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };
  const handleMainFocus = (event: FocusEvent<HTMLElement>): void => {
    if (isTextEntryControl(event.target)) setIsTextEntryFocused(true);
  };
  const handleMainBlur = (event: FocusEvent<HTMLElement>): void => {
    if (isTextEntryControl(event.target) && !isTextEntryControl(event.relatedTarget)) {
      setIsTextEntryFocused(false);
    }
  };

  if (isLoading) {
    return (
      <div
        aria-label={t("auth.session.checking")}
        className="relative flex min-h-screen items-center justify-center overflow-hidden bg-canvas px-6"
        role="status"
      >
        <div
          aria-hidden="true"
          className="absolute -left-24 top-1/4 h-72 w-72 rounded-full bg-accent/10 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="absolute -right-24 bottom-1/4 h-72 w-72 rounded-full bg-highlight/10 blur-3xl"
        />
        <div className="relative flex w-full max-w-xs flex-col items-center gap-8">
          <div className="motion-safe:animate-pulse">
            <Brand showTagline />
          </div>
          <div
            aria-hidden="true"
            className="h-1 w-28 overflow-hidden rounded-full bg-line"
          >
            <div className="h-full w-1/2 animate-pulse rounded-full bg-accent motion-reduce:w-full motion-reduce:animate-none" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent">
      <a
        className="fixed left-3 top-3 z-[60] -translate-y-24 rounded-md bg-ink px-4 py-2 text-surface transition-transform focus:translate-y-0 focus:text-surface motion-reduce:transition-none"
        href="#main-content"
      >
        {t("common.Layout.skipToMainContent")}
      </a>
      <div aria-atomic="true" aria-live="polite" className="sr-only">
        {routeAnnouncement}
      </div>

      {isAuthenticated ? (
        <>
          <Sidebar
            fullName={user?.full_name}
            onLogout={handleLogout}
            username={user?.username}
          />
          <header className="sticky top-0 z-40 flex h-14 items-center border-b border-line/80 bg-surface/95 px-4 shadow-sm backdrop-blur-xl md:hidden">
            <Brand compact to="/dashboard" />
          </header>

          {isMenuOpen && (
            <div className="fixed inset-0 z-50 bg-ink/35 pt-14 backdrop-blur-[2px] md:hidden">
              <div
                aria-hidden="true"
                className="absolute inset-0 z-0"
                onClick={() => closeMenu(true)}
              />
              <div
                aria-label={t("common.Layout.navigationMenu")}
                aria-modal="true"
                className="relative z-10 ml-auto flex h-full w-[min(24rem,calc(100%-1rem))] flex-col overflow-y-auto rounded-tl-2xl border-l border-t border-line bg-surface px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 shadow-soft motion-safe:animate-fade-in"
                id={menuId}
                onKeyDown={handleMenuKeyDown}
                role="dialog"
              >
                <div className="mb-2 flex justify-end">
                  <button
                    aria-label={t("common.Layout.closeNavigationMenu")}
                    className="flex h-11 w-11 items-center justify-center rounded-xl text-ink transition-colors hover:bg-accent/10 focus:outline-none"
                    onClick={() => closeMenu(true)}
                    type="button"
                  >
                    <FiX aria-hidden="true" className="h-5 w-5" />
                  </button>
                </div>
                <nav
                  aria-label={t("common.Layout.mobileNavigation")}
                  className="space-y-1"
                >
                  {authenticatedNavigation.map(
                    ({ icon: Icon, labelId, path }, index) => (
                      <NavLink
                        className={mobileLinkClass}
                        key={path}
                        onClick={() => closeMenu()}
                        ref={index === 0 ? firstMenuLinkRef : undefined}
                        to={path}
                      >
                        <Icon aria-hidden="true" className="h-5 w-5" />
                        {t(labelId)}
                      </NavLink>
                    )
                  )}
                </nav>
                <div className="mt-auto border-t border-line pt-4">
                  <UserControls
                    fullName={user?.full_name}
                    onLogout={handleLogout}
                    username={user?.username}
                  />
                </div>
              </div>
            </div>
          )}

          <nav
            aria-label={t("common.Layout.mobileTabBar")}
            className={`${isTextEntryFocused ? "hidden" : "grid"} fixed inset-x-0 bottom-0 z-40 grid-cols-5 border-t border-line/80 bg-surface/95 px-1 pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_28px_rgb(28_59_61_/_0.08)] backdrop-blur-xl md:hidden`}
          >
            {mobilePrimaryNavigation.map(({ icon: Icon, labelId, path }) => (
              <NavLink
                className={({ isActive }) =>
                  `relative flex min-h-[4rem] min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-1 text-[0.6875rem] font-semibold leading-none focus:outline-none ${isActive ? "text-highlight after:absolute after:top-0 after:h-1 after:w-8 after:rounded-b-full after:bg-highlight" : "text-muted hover:bg-accent/[0.08] hover:text-ink"}`
                }
                key={path}
                to={path}
              >
                <Icon aria-hidden="true" className="h-5 w-5" />
                <span className="max-w-full truncate">{t(labelId)}</span>
              </NavLink>
            ))}
            <button
              aria-controls={menuId}
              aria-expanded={isMenuOpen}
              aria-label={t("common.navigation.more")}
              className="flex min-h-[4rem] min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-1 text-[0.6875rem] font-semibold leading-none text-muted hover:bg-accent/[0.08] hover:text-ink focus:outline-none"
              onClick={() => setIsMenuOpen(true)}
              ref={menuButtonRef}
              type="button"
            >
              <FiMenu aria-hidden="true" className="h-5 w-5" />
              <span>{t("common.navigation.more")}</span>
            </button>
          </nav>
          {user?.id != null && <GlobalReadingTimer userId={user.id} />}
          {user?.id != null && <QuickReadingAction userId={user.id} />}
        </>
      ) : (
        <header className="border-b border-line/80 bg-surface/90 shadow-sm backdrop-blur-xl">
          <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-2 px-3 min-[360px]:px-4 sm:px-6 lg:px-8">
            <Brand showTagline />
            <nav
              aria-label={t("common.Layout.accountNavigation")}
              className="flex items-center gap-2 sm:gap-3"
            >
              <div className="flex items-center rounded-xl border border-line bg-canvas/50 p-0.5">
                <LanguageSelector iconOnly />
                <ThemeToggle />
              </div>
              <NavLink
                aria-label={t("common.Layout.login")}
                className="inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm font-medium text-ink transition-colors hover:bg-accent/10 hover:text-highlight min-[440px]:px-3"
                to="/login"
              >
                <FiLogIn aria-hidden="true" className="h-5 w-5" />
                <span className="hidden min-[440px]:inline">
                  {t("common.Layout.login")}
                </span>
              </NavLink>
              <NavLink
                aria-label={t("common.Layout.register")}
                className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-ink bg-ink px-3 py-2 text-sm font-semibold text-surface shadow-sm transition-all hover:-translate-y-0.5 hover:border-accent hover:bg-accent hover:text-surface hover:shadow-card sm:px-4"
                to="/register"
              >
                <FiUserPlus aria-hidden="true" className="h-5 w-5" />
                <span className="hidden min-[520px]:inline">
                  {t("common.Layout.register")}
                </span>
              </NavLink>
            </nav>
          </div>
        </header>
      )}

      <main
        className={
          isAuthenticated
            ? "md:ml-sidebar min-w-0 pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0"
            : "min-w-0"
        }
        id="main-content"
        onBlurCapture={handleMainBlur}
        onFocusCapture={handleMainFocus}
        ref={mainRef}
        tabIndex={-1}
      >
        <div
          className={
            isAuthenticated
              ? "mx-auto w-full max-w-[82rem] px-3 py-4 min-[380px]:px-4 sm:px-6 sm:py-6 md:px-8 md:py-8 lg:px-12 lg:py-10"
              : "mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10"
          }
        >
          <Outlet />
        </div>
      </main>
    </div>
  );
}
function getRouteLabel(pathname: string, t: (id: MessageId) => string): string {
  const authenticatedLabel = authenticatedNavigation.find(
    ({ path }) => pathname === path
  )?.labelId;
  if (authenticatedLabel) return t(authenticatedLabel);
  if (pathname.startsWith("/books/")) return t("common.routes.bookDetails");
  if (pathname === "/settings") return t("common.navigation.settings");
  if (pathname === "/login") return t("common.routes.login");
  if (pathname === "/register") return t("common.routes.createAccount");
  if (pathname === "/") return t("common.routes.home");
  return t("common.routes.notFound");
}
